import { useState, useEffect, useMemo } from 'react'

// Breakpoint values in pixels (matching theme.breakpoints)
const BREAKPOINTS = {
  mobile: 640,
  tablet: 1024,
  desktop: 1280,
} as const

export type BreakpointKey = keyof typeof BREAKPOINTS

export interface BreakpointState {
  /** Current viewport width in pixels */
  width: number
  /** True when viewport < 640px */
  isMobile: boolean
  /** True when viewport >= 640px and < 1024px */
  isTablet: boolean
  /** True when viewport >= 1024px */
  isDesktop: boolean
  /** True when viewport >= 1280px */
  isWideDesktop: boolean
  /** True when device has touch capability */
  isTouch: boolean
  /** True when device prefers reduced motion */
  prefersReducedMotion: boolean
  /** Current breakpoint name */
  breakpoint: 'mobile' | 'tablet' | 'desktop' | 'wideDesktop'
}

/**
 * Get current viewport width (SSR-safe)
 */
const getViewportWidth = (): number => {
  if (typeof window === 'undefined') return 0
  return window.innerWidth
}

/**
 * Check if device has touch capability
 */
const checkIsTouch = (): boolean => {
  if (typeof window === 'undefined') return false
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  )
}

/**
 * Check if user prefers reduced motion
 */
const checkPrefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Calculate breakpoint state from viewport width
 */
const calculateBreakpointState = (
  width: number
): Omit<BreakpointState, 'width' | 'isTouch' | 'prefersReducedMotion'> => {
  const isMobile = width < BREAKPOINTS.mobile
  const isTablet = width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet
  const isDesktop = width >= BREAKPOINTS.tablet
  const isWideDesktop = width >= BREAKPOINTS.desktop

  let breakpoint: BreakpointState['breakpoint'] = 'mobile'
  if (isWideDesktop) breakpoint = 'wideDesktop'
  else if (isDesktop) breakpoint = 'desktop'
  else if (isTablet) breakpoint = 'tablet'

  return { isMobile, isTablet, isDesktop, isWideDesktop, breakpoint }
}

/**
 * Hook to detect current viewport breakpoint and device capabilities.
 *
 * Uses mobile-first breakpoints:
 * - mobile: < 640px
 * - tablet: 640px - 1023px
 * - desktop: 1024px - 1279px
 * - wideDesktop: >= 1280px
 *
 * @example
 * ```tsx
 * const { isMobile, isDesktop, isTouch } = useBreakpoint()
 *
 * return isMobile ? <MobileView /> : <DesktopView />
 * ```
 */
export const useBreakpoint = (): BreakpointState => {
  const [width, setWidth] = useState<number>(getViewportWidth)
  const [isTouch, setIsTouch] = useState<boolean>(checkIsTouch)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(
    checkPrefersReducedMotion
  )

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleResize = () => {
      setWidth(window.innerWidth)
    }

    // Update touch detection (could change if external device connected)
    const handlePointerChange = (e: MediaQueryListEvent) => {
      setIsTouch(e.matches || 'ontouchstart' in window)
    }

    // Update reduced motion preference
    const handleMotionChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches)
    }

    // Listen for resize
    window.addEventListener('resize', handleResize)

    // Listen for pointer type changes
    const pointerQuery = window.matchMedia('(pointer: coarse)')
    pointerQuery.addEventListener('change', handlePointerChange)

    // Listen for motion preference changes
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    motionQuery.addEventListener('change', handleMotionChange)

    // Initial check
    setIsTouch(checkIsTouch())
    setPrefersReducedMotion(checkPrefersReducedMotion())

    return () => {
      window.removeEventListener('resize', handleResize)
      pointerQuery.removeEventListener('change', handlePointerChange)
      motionQuery.removeEventListener('change', handleMotionChange)
    }
  }, [])

  const breakpointState = useMemo(
    () => calculateBreakpointState(width),
    [width]
  )

  return {
    width,
    ...breakpointState,
    isTouch,
    prefersReducedMotion,
  }
}

/**
 * Hook to check if viewport matches a specific breakpoint or range.
 *
 * @param query - Breakpoint query like 'mobile', 'tablet', '>mobile', '<desktop'
 * @returns Boolean indicating if query matches
 *
 * @example
 * ```tsx
 * const isSmall = useMatchBreakpoint('<tablet')
 * const isLarge = useMatchBreakpoint('>=desktop')
 * ```
 */
export const useMatchBreakpoint = (query: string): boolean => {
  const { width } = useBreakpoint()

  return useMemo(() => {
    // Parse query like 'mobile', '<tablet', '>=desktop'
    const match = query.match(/^([<>]=?)?(\w+)$/)
    if (!match) return false

    const [, operator, breakpointName] = match
    const breakpointValue = BREAKPOINTS[breakpointName as BreakpointKey]
    if (breakpointValue === undefined) return false

    switch (operator) {
      case '<':
        return width < breakpointValue
      case '<=':
        return width <= breakpointValue
      case '>':
        return width > breakpointValue
      case '>=':
        return width >= breakpointValue
      default:
        // Exact match: within this breakpoint range
        if (breakpointName === 'mobile') return width < BREAKPOINTS.mobile
        if (breakpointName === 'tablet')
          return width >= BREAKPOINTS.mobile && width < BREAKPOINTS.tablet
        if (breakpointName === 'desktop')
          return width >= BREAKPOINTS.tablet && width < BREAKPOINTS.desktop
        return width >= breakpointValue
    }
  }, [width, query])
}

export default useBreakpoint
